import * as stylex from "@stylexjs/stylex";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, LayoutGrid, List, PanelRightClose, PanelRightOpen, SlidersHorizontal } from "lucide-react";
import type { DeliveryState, MeasurementState } from "@aco/core";
import type { ActiveBreakdown } from "@/lib/reporting-breakdowns";
import Button, { IconButton } from "@/components/ui/Button";
import MenuSelect from "@/components/ui/MenuSelect";
import Popover from "@/components/ui/Popover";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";
import HoverCard from "@/components/launch/shell/HoverCard";
import HoverVideo from "@/components/launch/shell/HoverVideo";
import CreativePeek from "./CreativePeek";
import { useRememberedState, viewStateKey } from "@/lib/remembered-view-state";
import { formatMetricValue } from "@/lib/grid-format";
import { entityTypeLabel } from "@/lib/creative-entity";
import { clickThroughRate, defaultBreakdownSortDirection, sortBreakdownRows } from "@/lib/breakdown-sort";
import { isRenderablePreviewUrl } from "@/lib/creative-preview";
import type { BreakdownRow, BreakdownSort, BreakdownSortKey } from "@/lib/breakdown-sort";
import type { ExtraMetric } from "@/lib/extra-metrics";
import { availableBreakdownColumns, BREAKDOWN_COLUMNS, DEFAULT_BREAKDOWN_METRICS, loadBreakdownLayout, saveBreakdownLayout, type BreakdownColumn, type BreakdownKind, type BreakdownMetricKey, type ExtraColumnKey } from "@/lib/breakdown-columns";
const styles = {
  sortButton: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xsuxsxl",
    k8WAf4: "x1cm8czd",
    kMwMTN: "x1heor9g xfpavck x5hq0bf",
    $$css: true
  },
  expand: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    kaIpWk: "x1wpy2cq",
    kSiTet: "x3onkmb x1i6vvy0 xe8f8ch",
    $$css: true
  },
  expandOn: {
    kWkggS: "xlcwlql",
    kMwMTN: "xgwkh9f",
    kSiTet: "x1hc1fzr xepoxe xe8f8ch",
    $$css: true
  },
  nameButton: {
    k9WMMc: "xdpxx8g",
    kybGjl: "x1hl2dhg xt0b8zv",
    kcSHmL: "xda060z",
    $$css: true
  },
  pickerFoot: {
    kLKAdn: "x67pdoy",
    $$css: true
  },
  segment: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "xsuxsxl",
    kZKoxP: "x5yr21d",
    kg3NbH: "xtft4pz",
    kaIpWk: "x3kktt9",
    kGuDYH: "x7x6yu3",
    k63SB2: "xk50ysn",
    kMwMTN: "x1i6qc9y xfpavck",
    $$css: true
  },
  segmentOn: {
    kWkggS: "x1h1f4i9",
    kGVxlE: "x17ljfxq",
    kMwMTN: "xgwkh9f xfpavck",
    $$css: true
  },
  compareName: {
    k1xSpc: "x1lliihq",
    ks0D6T: "x193iq5w",
    kMwMTN: "xgwkh9f",
    kGuDYH: "xtu3avg",
    k63SB2: "x1s688f",
    kLWn49: "x1xfvgam",
    k9WMMc: "xdpxx8g",
    $$css: true
  },
  compareMetrics: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x185a7wo",
    k1C7PZ: "x1bclm3k",
    khm7nJ: "xba9wbz",
    kogj98: "x1ghz6dp",
    kLKAdn: "xfisy6v",
    $$css: true
  },
  compareParts: {
    kumcoG: "xnby9oq",
    $$css: true
  },
  compareTerm: {
    kMwMTN: "x1i6qc9y",
    kGuDYH: "x1hqrb6m",
    $$css: true
  }
};
function platformLabel(platform: BreakdownRow["platform"]) {
  return platform === "tiktok" ? "TikTok" : platform === "meta" ? "Meta" : "Google";
}

// Track preview URLs whose <img> failed to load so a broken, expired, or
// unsupported thumbnail collapses to the neutral fallback instead of a broken
// image element (issue 14). Keyed by URL so re-enrichment with a fresh URL
// gets another attempt.
function useFailedPreviews() {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set<string>());
  const markFailed = (url: string | null | undefined) => {
    if (!url) return;
    setFailed(prev => prev.has(url) ? prev : new Set(prev).add(url));
  };
  return {
    failed,
    markFailed
  };
}
export type CreativeLayout = "list" | "compare";
const MIN_CREATIVE_SPEND = 100;
const NO_BREAKDOWNS: ActiveBreakdown[] = [];
const EMPTY_FILTERS: Record<string, string[]> = {};
const DEFAULT_SORT: BreakdownSort = {
  key: "spend",
  direction: "desc"
};
type CompareColumns = "auto" | 2 | 3 | 4 | 5 | 6;
const COMPARE_COLUMN_OPTIONS: Array<{
  value: string;
  label: string;
}> = [{
  value: "auto",
  label: "Auto columns"
}, ...[2, 3, 4, 5, 6].map(n => ({
  value: String(n),
  label: `${n} per row`
}))];
function metricColumnWidth(column: BreakdownColumn) {
  if (column.key === "costPerConversion") return 170;
  if (column.key === "conversions") return 150;
  return column.kind === "status" ? 130 : 110;
}
export function resolveAssignmentColumns(breakdowns: ActiveBreakdown[], filters: Record<string, string[]>, scope: string) {
  return breakdowns.map(definition => {
    const key = `${scope}:${definition.id}`;
    const requested = new Set(filters[key]);
    const selected = definition.results.filter(result => requested.has(result.id));
    const values = selected.map(result => result.id);
    return {
      ...definition,
      key,
      values,
      selected: new Set(values),
      label: `${definition.name}: ${selected.length ? selected.map(result => result.name).join(", ") : "All"}`,
      options: [{
        value: "",
        label: "All assignments"
      }, ...definition.results.map(result => ({
        value: result.id,
        label: result.name
      }))]
    };
  });
}
function AssignmentDropdowns({
  columns,
  onChange
}: {
  columns: ReturnType<typeof resolveAssignmentColumns>;
  onChange: (key: string, values: string[]) => void;
}) {
  return <>
      <div className="x3nfvp2 x1a02dak x6s0dn4 x166cprz" data-export-controls>
        {columns.map(column => <MenuSelect key={column.id} value="" selectedValues={column.values.length ? column.values : [""]} selectionLabel={column.label} ariaLabel={`Filter ${column.name} assignments`} options={column.options} onValueChange={id => onChange(column.key, !id ? [] : column.selected.has(id) ? column.values.filter(value => value !== id) : [...column.values, id])} />)}
      </div>
    </>;
}
function AssignmentScopeNote({
  columns
}: {
  columns: ReturnType<typeof resolveAssignmentColumns>;
}) {
  // Only worth saying once an assignment filter narrows the rows.
  if (!columns.some(column => column.values.length > 0)) return null;
  return <p className="x1ghz6dp x1sf1iou x1i6qc9y x7x6yu3 x1evy7pa" data-export-note>
      Metrics are full entity totals within the global Filter and report selection. Assignment
      filters select entities, not portions of their totals.
      {columns.flatMap(column => column.values.length ? [` ${column.label}.`] : []).join("")}
    </p>;
}
const DELIVERY_LABEL: Record<DeliveryState, string> = {
  delivering: "Delivering",
  inactive: "Not delivering",
  none: "No delivery"
};

// How a single Mapped conversion part renders in the Conversions expansion. A
// measured zero shows `0`; an unmeasured or incomplete range shows an em dash
// with the reason, never a fabricated zero.
function measuredCountCell(part: {
  value: number;
  state: MeasurementState;
}, currency: string): {
  text: string;
  title?: string;
  absent: boolean;
} {
  switch (part.state) {
    case "measured":
    case "provisional":
      return {
        text: formatMetricValue(part.value, "number", currency),
        absent: false
      };
    case "zero":
      return {
        text: "0",
        absent: false
      };
    case "incomplete":
      return {
        text: "—",
        title: "Incomplete",
        absent: true
      };
    case "unconfigured":
      return {
        text: "—",
        title: "Not configured",
        absent: true
      };
    default:
      return {
        text: "—",
        title: "Not received",
        absent: true
      };
  }
}

// Cost per one Mapped conversion part. Absent (an em dash) whenever the
// denominator was not a measured positive count — a coverage hole, an unmeasured
// range, or a measured zero all mean spend / count is not a real cost.
function costPerPartCell(part: {
  value: number;
  state: MeasurementState;
}, spend: number, currency: string): {
  text: string;
  title?: string;
  absent: boolean;
} {
  const measured = part.state === "measured" || part.state === "provisional";
  if (measured && part.value > 0) {
    return {
      text: formatMetricValue(spend / part.value, "currency_per", currency),
      absent: false
    };
  }
  const title = part.state === "incomplete" ? "Incomplete" : part.state === "zero" ? "No conversions" : part.state === "unconfigured" ? "Not configured" : "Not received";
  return {
    text: "—",
    title,
    absent: true
  };
}
export function CreativeLayoutControl({
  value,
  onChange
}: {
  value: CreativeLayout;
  onChange: (layout: CreativeLayout) => void;
}) {
  return <div role="group" aria-label="Creative view" className="x3nfvp2 x6s0dn4 x3d5knr x5esxvw xyeyesb x3kktt9 xlcwlql">
      {([["list", "List", <List key="icon" aria-hidden size={13} />], ["compare", "Compare", <LayoutGrid key="icon" aria-hidden size={13} />]] as const).map(([option, label, icon]) => <button key={option} type="button" aria-pressed={value === option} onClick={() => onChange(option)} {...stylex.props(recipes.bareButton, styles.segment, recipes.focusRing, recipes.transition, value === option && styles.segmentOn)}>
          {icon} {label}
        </button>)}
    </div>;
}

// Admin-free, per-user Display control: pick which configurable metrics show as
// columns. The choice is remembered in the user's own browser, so one user's
// layout never mutates another's.
function ColumnPicker({
  columns,
  selected,
  onChange
}: {
  /** The columns this Company may show (extra metrics only when opted into). */
  columns: BreakdownColumn[];
  selected: BreakdownMetricKey[];
  onChange: (metrics: BreakdownMetricKey[]) => void;
}) {
  const selectedSet = new Set(selected);
  const toggle = (key: BreakdownMetricKey) => {
    const next = BREAKDOWN_COLUMNS.filter(column => column.key === key ? !selectedSet.has(key) : selectedSet.has(column.key)).map(column => column.key);
    onChange(next);
  };
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  return <>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={<SlidersHorizontal size={13} />} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen(current => !current)}>
        Columns
      </Button>
      <Popover open={open} anchorRef={triggerRef} onClose={close} align="end" role="group" aria-label="Table columns">
        <div className="x78zum5 xdt5ytf xsuxsxl x16grhtn xt5iall">
          {columns.map(column => <label key={column.key} className="x78zum5 x6s0dn4 x166cprz xe0p6wg xtu3avg xgwkh9f x1ypdohk">
              <input type="checkbox" checked={selectedSet.has(column.key)} onChange={() => toggle(column.key)} />
              {column.label}
            </label>)}
          <div {...stylex.props(styles.pickerFoot, recipes.hairlineTopSoft)}>
            <Button variant="ghost" size="sm" onClick={() => onChange([...DEFAULT_BREAKDOWN_METRICS])}>
              Reset to defaults
            </Button>
          </div>
        </div>
      </Popover>
    </>;
}
const NO_EXTRA_METRICS: readonly ExtraMetric[] = [];
export default function EntityBreakdown({
  rows,
  kind,
  currency,
  orgSlug = "default",
  layout = "list",
  onNameClick,
  onSelect,
  breakdowns = NO_BREAKDOWNS,
  assignmentFilters: controlledAssignmentFilters,
  onAssignmentFiltersChange,
  extraMetrics = NO_EXTRA_METRICS,
  extraValues,
  onExtraColumnsShown
}: {
  rows: BreakdownRow[];
  breakdowns?: ActiveBreakdown[];
  kind: BreakdownKind;
  currency: string;
  orgSlug?: string;
  layout?: CreativeLayout;
  onNameClick?: (row: BreakdownRow) => void;
  // Opens the addressable same-page detail state for a creative row (issue 13).
  onSelect?: (row: BreakdownRow) => void;
  // When the page owns the assignment filters (so they can sit in the
  // reporting toolbar), it passes them in; otherwise the breakdown keeps its
  // own remembered copy and renders the dropdowns itself.
  assignmentFilters?: Record<string, string[]>;
  onAssignmentFiltersChange?: (next: Record<string, string[]>) => void;
  /** The extra metrics the Company opted into: their columns are offered. */
  extraMetrics?: readonly ExtraMetric[];
  /** Their values, keyed `platform:campaignId` or `platform:adId`. */
  extraValues?: Record<string, Partial<Record<ExtraColumnKey, number | null>>>;
  /** Whether an extra column is showing, so its values are read only when needed. */
  onExtraColumnsShown?: (shown: boolean) => void;
}) {
  const {
    failed: failedPreviews,
    markFailed
  } = useFailedPreviews();
  const [metrics, setMetrics] = useState<BreakdownMetricKey[]>(() => loadBreakdownLayout(orgSlug, kind) ?? DEFAULT_BREAKDOWN_METRICS);
  // Restore the remembered layout when the workspace or entity kind changes.
  useEffect(() => {
    setMetrics(loadBreakdownLayout(orgSlug, kind) ?? [...DEFAULT_BREAKDOWN_METRICS]);
  }, [orgSlug, kind]);
  const updateMetrics = (next: BreakdownMetricKey[]) => {
    setMetrics(next);
    saveBreakdownLayout(orgSlug, kind, next);
  };

  // Each view (By campaign, By creative) remembers its own controls per
  // workspace and device, so moving between views and back keeps them.
  const remember = (name: string) => viewStateKey(orgSlug, kind, name);
  const [showConversionParts, setShowConversionParts] = useRememberedState(remember("conversion-parts"), false);
  const [showCostParts, setShowCostParts] = useRememberedState(remember("cost-parts"), false);
  const [showLowSpendCreatives, setShowLowSpendCreatives] = useRememberedState(remember("low-spend"), false);
  const [sort, setSort] = useRememberedState<BreakdownSort>(remember("sort"), DEFAULT_SORT);
  const [compareColumns, setCompareColumns] = useRememberedState<CompareColumns>(remember("compare-columns"), "auto");
  const [ownAssignmentFilters, setOwnAssignmentFilters] = useRememberedState<Record<string, string[]>>(remember("assignments"), EMPTY_FILTERS);
  const assignmentFilters = controlledAssignmentFilters ?? ownAssignmentFilters;
  const assignmentColumns = useMemo(() => resolveAssignmentColumns(breakdowns, assignmentFilters, `${orgSlug}:${kind}`), [breakdowns, assignmentFilters, orgSlug, kind]);
  const hasAssignmentFilter = assignmentColumns.some(column => column.selected.size > 0);
  const hiddenLowSpendCount = useMemo(() => kind === "creative" ? rows.filter(row => row.spend < MIN_CREATIVE_SPEND).length : 0, [kind, rows]);
  const columns = useMemo(() => availableBreakdownColumns(extraMetrics), [extraMetrics]);
  const extraShown = columns.some(column => column.extra && metrics.includes(column.key));
  useEffect(() => onExtraColumnsShown?.(extraShown), [extraShown, onExtraColumnsShown]);
  const withExtras = useMemo(() => extraValues ? rows.map(row => ({
    ...row,
    extra: extraValues[`${row.platform}:${kind === "campaign" ? row.campaignId : row.adId}`]
  })) : rows, [extraValues, kind, rows]);
  const visibleRows = useMemo(() => kind === "creative" && !showLowSpendCreatives ? withExtras.filter(row => row.spend >= MIN_CREATIVE_SPEND) : withExtras, [kind, withExtras, showLowSpendCreatives]);
  // Every configured Mapped conversion the server attached to the rows, in a
  // stable order (the rows already carry every part, measured zero included).
  const conversionParts = useMemo(() => {
    const names: string[] = [];
    const seen = new Set<string>();
    for (const row of visibleRows) {
      for (const item of row.conversionDetails) {
        if (seen.has(item.name)) continue;
        seen.add(item.name);
        names.push(item.name);
      }
    }
    return names;
  }, [visibleRows]);
  const hasParts = conversionParts.length > 0;
  const selectedMetrics = useMemo(() => columns.filter(column => metrics.includes(column.key)), [columns, metrics]);
  const showsConversions = metrics.includes("conversions");
  const showsCost = metrics.includes("costPerConversion");
  const expandConversions = showsConversions && showConversionParts && hasParts;
  const expandCost = showsCost && showCostParts && hasParts;
  const sortedRows = sortBreakdownRows(visibleRows.filter(row => assignmentColumns.every(column => {
    return column.selected.size === 0 || row.assignments?.some(assignment => assignment.breakdownId === column.id && column.selected.has(assignment.resultId));
  })), sort);
  function updateSort(key: BreakdownSortKey) {
    setSort(current => current.key === key ? {
      key,
      direction: current.direction === "desc" ? "asc" : "desc"
    } : {
      key,
      direction: defaultBreakdownSortDirection(key)
    });
  }
  function renderSortIcon(key: BreakdownSortKey) {
    if (sort.key !== key) return <ArrowUpDown aria-hidden data-export-controls size={11} className="xuzhngd" />;
    return sort.direction === "desc" ? <ArrowDown aria-hidden data-export-controls size={11} /> : <ArrowUp aria-hidden data-export-controls size={11} />;
  }
  function renderSortButton(label: string, key: BreakdownSortKey) {
    return <button type="button" {...stylex.props(recipes.bareButton, styles.sortButton, recipes.focusRing)} aria-label={`Sort by ${label}, ${sort.key === key && sort.direction === "desc" ? "ascending" : "descending"}`} onClick={() => updateSort(key)}>
        <span>{label}</span>
        {renderSortIcon(key)}
      </button>;
  }

  // Expandable header cell (Conversions / cost per conversion): the sort control
  // plus a keyboard-operable expand toggle that inserts the Mapped parts.
  function renderExpandableHeader(column: BreakdownColumn, expanded: boolean, onToggle: () => void, expandLabel: string, collapseLabel: string) {
    return <div className="x78zum5 x6s0dn4 x13a6bvl x3d5knr">
        {renderSortButton(column.label, column.sortKey)}
        <button type="button" {...stylex.props(recipes.bareButton, styles.expand, recipes.focusRing, expanded && styles.expandOn)} data-export-controls aria-label={expanded ? collapseLabel : expandLabel} aria-expanded={expanded} disabled={!hasParts} onClick={onToggle}>
          {expanded ? <PanelRightClose aria-hidden size={14} /> : <PanelRightOpen aria-hidden size={14} />}
        </button>
      </div>;
  }
  const identityColumnCount = kind === "creative" ? 3 : 2;
  const tableColumnCount = identityColumnCount + breakdowns.length + selectedMetrics.length + (expandConversions ? conversionParts.length : 0) + (expandCost ? conversionParts.length : 0);
  const tableWidth = 260 + 90 + breakdowns.length * 170 + (kind === "creative" ? 90 : 0) + selectedMetrics.reduce((sum, column) => sum + metricColumnWidth(column), 0) + (expandConversions ? conversionParts.length * 120 : 0) + (expandCost ? conversionParts.length * 130 : 0);
  const table = <div className="xw2csxc" data-export-scroll>
      <table className="xh8yej3 x1vathgz x1gukg7c x140o2bo x1e3avs9 xtu3avg xss6m8b" style={{
      minWidth: `${tableWidth}px`
    }}>
        <colgroup>
          <col style={{
          width: 260
        }} />
          <col style={{
          width: 90
        }} />
          {kind === "creative" && <col style={{
          width: 90
        }} />}
          {breakdowns.map(definition => <col key={definition.id} style={{
          width: 170
        }} />)}
          {selectedMetrics.map(column => <col key={column.key} style={{
          width: metricColumnWidth(column)
        }} />)}
          {expandConversions && conversionParts.map(name => <col key={`c:${name}`} style={{
          width: 120
        }} />)}
          {expandCost && conversionParts.map(name => <col key={`cost:${name}`} style={{
          width: 130
        }} />)}
        </colgroup>
        <thead>
          <tr>
            <th aria-sort={sort.key === "name" ? `${sort.direction}ending` : "none"} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x1i6qc9y x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft">
              {renderSortButton(kind === "creative" ? "Creative" : "Campaign", "name")}
            </th>
            <th aria-sort={sort.key === "platform" ? `${sort.direction}ending` : "none"} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x1i6qc9y x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft">
              {renderSortButton("Platform", "platform")}
            </th>
            {kind === "creative" && <th aria-sort={sort.key === "format" ? `${sort.direction}ending` : "none"} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x1i6qc9y x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft">
                {renderSortButton("Format", "format")}
              </th>}
            {breakdowns.map(definition => <th key={definition.id} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x1i6qc9y x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft">
                {definition.name}
              </th>)}
            {selectedMetrics.map(column => {
            const numeric = column.kind !== "status";
            const ariaSort = sort.key === column.sortKey ? `${sort.direction}ending` as const : "none";
            if (column.key === "conversions") {
              return <th key={column.key} aria-sort={ariaSort} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x7x6yu3 xo1l8bm xxymvpz xuxw1ft xgwkh9f x1hr2gdg">
                    {renderExpandableHeader(column, showConversionParts, () => setShowConversionParts(value => !value), "Show conversion breakdown columns", "Hide conversion breakdown columns")}
                  </th>;
            }
            if (column.key === "costPerConversion") {
              return <th key={column.key} aria-sort={ariaSort} className="x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x7x6yu3 xo1l8bm xxymvpz xuxw1ft xgwkh9f x1hr2gdg">
                    {renderExpandableHeader(column, showCostParts, () => setShowCostParts(value => !value), "Show cost per conversion breakdown columns", "Hide cost per conversion breakdown columns")}
                  </th>;
            }
            return <th key={column.key} aria-sort={ariaSort} {...{
              0: {
                className: "x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x1i6qc9y x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft"
              },
              1: {
                className: "x1eryijn x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x7x6yu3 xo1l8bm xxymvpz xuxw1ft xgwkh9f x1hr2gdg"
              }
            }[!!numeric << 0]}>
                  {renderSortButton(column.label, column.sortKey)}
                </th>;
          })}
            {expandConversions && conversionParts.map(name => {
            const key = `conversion:${name}` as const;
            return <th key={`c:${name}`} aria-sort={sort.key === key ? `${sort.direction}ending` : "none"} className="x1eryijn xs86qnd xso031l x1q0q8m5 xaecqrk x7x6yu3 xo1l8bm xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d x1i6qc9y">
                    {renderSortButton(name, key)}
                  </th>;
          })}
            {expandCost && conversionParts.map(name => <th key={`cost:${name}`} className="x1eryijn xs86qnd xso031l x1q0q8m5 xaecqrk x7x6yu3 xo1l8bm xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d x1i6qc9y">
                  <span>{`Cost / ${name}`}</span>
                </th>)}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(row => {
          const rowKey = `${row.platform}:${row.accountId}:${row.adId ?? row.campaignId ?? row.name}`;
          const partByName = new Map(row.conversionDetails.map(item => [item.name, item]));
          return <tr key={rowKey} className="xjbqb8w xa2g735">
                <td className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xgwkh9f">
                  <div className="x78zum5 x6s0dn4 x166cprz xeuugli">
                    {kind === "creative" && row.thumbnailUrl && !failedPreviews.has(row.thumbnailUrl) ?
                // Hovering the poster peeks at the creative: the video
                // plays silently when reporting supplied a playable
                // source, else the poster shows large. Nothing loads
                // until the card opens.
                <HoverCard content={row.previewMediaUrl ? <HoverVideo src={row.previewMediaUrl} poster={row.thumbnailUrl} width={240} height={240} /> : <img src={row.thumbnailUrl} alt="" decoding="async" referrerPolicy="no-referrer" className="x1lliihq xafpxmx x1u5z0ei x1wpy2cq xlcwlql x19kjcj4" />}>
                        <img src={row.thumbnailUrl} alt="" loading="lazy" decoding="async" fetchPriority="low" width={28} height={28} onError={() => markFailed(row.thumbnailUrl)} referrerPolicy="no-referrer" className="xgd8bvy x1fgtraw x2lah0s x1wpy2cq xlcwlql xl1xv1r" />
                      </HoverCard> : null}
                    {kind === "campaign" && row.campaignId && onNameClick ? <button type="button" {...stylex.props(recipes.bareButton, recipes.truncate, styles.nameButton, recipes.focusRing)} title={`Show creatives for ${row.name}`} onClick={() => onNameClick(row)}>
                        {row.name}
                      </button> : kind === "creative" && onSelect ? <button type="button" {...stylex.props(recipes.bareButton, recipes.truncate, styles.nameButton, recipes.focusRing)} title={`Open ${row.name}`} onClick={() => onSelect(row)}>
                        {row.name}
                      </button> : <span {...stylex.props(recipes.truncate)}>{row.name}</span>}
                    {/* Only an Asset is labelled: every other row is an ad. */}
                    {kind === "creative" && row.entityType === "asset" ? <span {...{
                  0: {
                    className: "x3nfvp2 x6s0dn4 x2lah0s x138t5iz xmkeg23 x1y0btm7 xzbo3x7 x3kktt9 x1i6qc9y x1hqrb6m xk50ysn x37zpob"
                  },
                  1: {
                    className: "x3nfvp2 x6s0dn4 x2lah0s x138t5iz xmkeg23 x1y0btm7 x3kktt9 x1hqrb6m xk50ysn x37zpob x10j0gwe xnclacp"
                  }
                }[!!!row.additive << 0]} data-additive={row.additive ? undefined : "false"} title={row.additive ? undefined : "Performance Max asset — several assets can share one delivery, so these rows are not additive."}>
                        {entityTypeLabel(row.entityType)}
                      </span> : null}
                  </div>
                </td>
                <td className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1i6qc9y">{platformLabel(row.platform)}</td>
                {kind === "creative" && <td className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1i6qc9y">{row.format}</td>}
                {breakdowns.map(definition => <td key={definition.id} className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft">
                    <AssignmentChips row={row} breakdownId={definition.id} />
                  </td>)}
                {selectedMetrics.map(column => {
              if (column.kind === "status") {
                return <td key={column.key} className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft">
                        <span data-state={row.deliveryState} {...{
                    0: {
                      className: "x3nfvp2 x6s0dn4 xsuxsxl x1i6qc9y x7x6yu3 xuxw1ft"
                    },
                    1: {
                      className: "x3nfvp2 x6s0dn4 xsuxsxl x7x6yu3 xuxw1ft xgwkh9f"
                    }
                  }[!!(row.deliveryState === "delivering") << 0]}>
                          <span aria-hidden {...{
                      0: {
                        className: "xci0xqf x1hagigm x2lah0s x3kktt9 x1u2vnfd"
                      },
                      2: {
                        className: "xci0xqf x1hagigm x2lah0s x3kktt9 x1etxgw"
                      },
                      1: {
                        className: "xci0xqf x1hagigm x2lah0s x3kktt9 xjbqb8w xmkeg23 x1y0btm7 xzbo3x7"
                      },
                      3: {
                        className: "xci0xqf x1hagigm x2lah0s x3kktt9 xjbqb8w xmkeg23 x1y0btm7 xzbo3x7"
                      }
                    }[!!(row.deliveryState === "delivering") << 1 | !!(row.deliveryState === "none") << 0]} />
                          {DELIVERY_LABEL[row.deliveryState]}
                        </span>
                      </td>;
              }
              const value = column.value ? column.value(row) : null;
              return <td key={column.key} className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xgwkh9f x1hr2gdg">
                      {column.format ? column.format(value, currency) : "—"}
                    </td>;
            })}
                {expandConversions && conversionParts.map(name => {
              const part = partByName.get(name) ?? {
                value: 0,
                state: "unmeasured" as MeasurementState
              };
              const cell = measuredCountCell(part, currency);
              return <td key={`c:${name}`} title={cell.title} {...{
                0: {
                  className: "x1eryijn xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d x1i6qc9y"
                },
                1: {
                  className: "x1eryijn xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d xovanlf x1146j2"
                }
              }[!!cell.absent << 0]}>
                        {cell.text}
                      </td>;
            })}
                {expandCost && conversionParts.map(name => {
              const part = partByName.get(name) ?? {
                value: 0,
                state: "unmeasured" as MeasurementState
              };
              const cell = costPerPartCell(part, row.spend, currency);
              return <td key={`cost:${name}`} title={cell.title} {...{
                0: {
                  className: "x1eryijn xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d x1i6qc9y"
                },
                1: {
                  className: "x1eryijn xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg xqmy9lo x19qxo9d xovanlf x1146j2"
                }
              }[!!cell.absent << 0]}>
                        {cell.text}
                      </td>;
            })}
              </tr>;
        })}
          {sortedRows.length === 0 && <tr>
              <td colSpan={Math.max(1, tableColumnCount)} className="x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xvr2c1s x2b8uid x1i6qc9y">
                {hasAssignmentFilter ? "No entities match these assignments." : kind === "creative" && rows.length > 0 ? `No creatives reached ${formatMetricValue(MIN_CREATIVE_SPEND, "currency", currency)} spend in this period.` : `No ${kind} data in this period.`}
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
  const displayControls = <ColumnPicker columns={columns} selected={metrics} onChange={updateMetrics} />;
  const assignmentControls = breakdowns.length > 0 && <>
      {controlledAssignmentFilters ? null : <AssignmentDropdowns columns={assignmentColumns} onChange={(key, values) => {
      const next = {
        ...assignmentFilters,
        [key]: values
      };
      if (onAssignmentFiltersChange) onAssignmentFiltersChange(next);else setOwnAssignmentFilters(next);
    }} />}
      <AssignmentScopeNote columns={assignmentColumns} />
    </>;
  if (kind === "campaign") {
    return <div className="x78zum5 xdt5ytf x166cprz xeuugli">
        {assignmentControls}
        <div className="x78zum5 x6s0dn4 x1qughib x5vihii x19d9pmb" data-export-controls>
          <div className="x78zum5 x6s0dn4 x166cprz xvc5jky">{displayControls}</div>
        </div>
        {table}
      </div>;
  }
  return <div className="x78zum5 xdt5ytf x166cprz xeuugli">
      {assignmentControls}
      {sortedRows.some(row => !row.additive) && <p className="x1ghz6dp x1sf1iou x1i6qc9y x7x6yu3 x1evy7pa" data-export-note>
          Performance Max asset rows are not additive: several assets can share one delivery. Do not
          sum them into campaign totals.
        </p>}
      <div className="x78zum5 x6s0dn4 x1qughib x5vihii x19d9pmb" data-export-controls>
        {hiddenLowSpendCount > 0 ? <Button variant="ghost" size="sm" onClick={() => setShowLowSpendCreatives(current => !current)}>
            {showLowSpendCreatives ? "Hide low spend" : `Show all (${hiddenLowSpendCount} below ${formatMetricValue(MIN_CREATIVE_SPEND, "currency", currency)})`}
          </Button> : null}
        {layout === "list" ? <div className="x78zum5 x6s0dn4 x166cprz xvc5jky">{displayControls}</div> : <div className="xvc5jky x78zum5 x6s0dn4 x166cprz">
            {hasParts ? <Button variant="ghost" size="sm" aria-pressed={showConversionParts} onClick={() => setShowConversionParts(value => !value)}>
                {showConversionParts ? "Hide conversion breakdown" : "Show conversion breakdown"}
              </Button> : null}
            <MenuSelect ariaLabel="Columns per row" value={String(compareColumns)} options={COMPARE_COLUMN_OPTIONS} onValueChange={value => setCompareColumns(value === "auto" ? "auto" : Number(value) as CompareColumns)} />
            <CompareSortControl sort={sort} onSort={key => {
          if (key !== sort.key) updateSort(key);
        }} onDirection={() => setSort(current => ({
          ...current,
          direction: current.direction === "desc" ? "asc" : "desc"
        }))} />
          </div>}
      </div>
      {layout === "list" ? table : <CompareLayout breakdowns={breakdowns} rows={sortedRows} currency={currency} conversionParts={conversionParts} showConversionParts={showConversionParts} columns={compareColumns} onSelect={onSelect} emptyMessage={rows.length > 0 ? `No creatives reached ${formatMetricValue(MIN_CREATIVE_SPEND, "currency", currency)} spend in this period.` : "No creative data in this period."} />}
    </div>;
}
function AssignmentChips({
  row,
  breakdownId
}: {
  row: BreakdownRow;
  breakdownId: string;
}) {
  const assignments = row.assignments?.filter(assignment => assignment.breakdownId === breakdownId) ?? [];
  return <div className="x78zum5 x1a02dak xsuxsxl xeaf4i8" data-export-value={assignments.map(assignment => assignment.name).join("; ") || "—"}>
      {assignments.length ? assignments.map(assignment => <span className="x1rg5ohu xsplgde x1wa2ap4 x3kktt9 xlcwlql x1e3avs9 x7x6yu3 xj0a0fe" key={assignment.resultId}>
              {assignment.name}
            </span>) : "—"}
    </div>;
}

// The creative-only "Compare" layout: one card per creative in a grid that
// fills the width and scrolls down with the page. Every card shows its media
// 1:1 so the grid stays even; hovering the media opens the creative at its own
// aspect ratio, playing the video when one is available. It keeps a fixed
// metric vocabulary (issue 13 deepens creative reporting); the configurable
// columns above drive the list layout for both kinds.
const COMPARE_SORT_OPTIONS: Array<{
  value: BreakdownSortKey;
  label: string;
}> = [{
  value: "spend",
  label: "Spend"
}, {
  value: "impressions",
  label: "Impressions"
}, {
  value: "clicks",
  label: "Clicks"
}, {
  value: "ctr",
  label: "CTR"
}, {
  value: "conversions",
  label: "Conversions"
}, {
  value: "costPerConversion",
  label: "Cost / conversion"
}, {
  value: "name",
  label: "Name"
}, {
  value: "firstSeen",
  label: "First seen"
}];
function CompareSortControl({
  sort,
  onSort,
  onDirection
}: {
  sort: BreakdownSort;
  onSort: (key: BreakdownSortKey) => void;
  onDirection: () => void;
}) {
  return <div className="x78zum5 x6s0dn4 xsuxsxl" data-export-controls>
      <MenuSelect ariaLabel="Sort by" value={sort.key} options={COMPARE_SORT_OPTIONS} selectionLabel={`Sort: ${COMPARE_SORT_OPTIONS.find(o => o.value === sort.key)?.label ?? "Spend"}`} onValueChange={value => onSort(value as BreakdownSortKey)} />
      <IconButton variant="secondary" aria-label={sort.direction === "desc" ? "Sort ascending" : "Sort descending"} onClick={onDirection}>
        {sort.direction === "desc" ? <ArrowDown aria-hidden size={14} /> : <ArrowUp aria-hidden size={14} />}
      </IconButton>
    </div>;
}
function CompareLayout({
  breakdowns,
  rows,
  currency,
  conversionParts,
  showConversionParts,
  columns,
  onSelect,
  emptyMessage
}: {
  breakdowns: ActiveBreakdown[];
  rows: BreakdownRow[];
  currency: string;
  conversionParts: string[];
  showConversionParts: boolean;
  columns: CompareColumns;
  onSelect?: (row: BreakdownRow) => void;
  emptyMessage: string;
}) {
  const {
    failed: failedPreviews,
    markFailed
  } = useFailedPreviews();
  const showParts = showConversionParts && conversionParts.length > 0;
  if (rows.length === 0) return <p className="x1ghz6dp xvr2c1s x2b8uid x1i6qc9y">{emptyMessage}</p>;
  const cellFor = (row: BreakdownRow, partName: string) => {
    const part = row.conversionDetails.find(item => item.name === partName);
    return measuredCountCell(part ?? {
      value: 0,
      state: "unmeasured"
    }, currency);
  };
  const ctrText = (row: BreakdownRow) => {
    const ctr = clickThroughRate(row);
    return ctr == null ? "—" : `${ctr.toFixed(2)}%`;
  };
  const costText = (row: BreakdownRow) => row.costPerConversion == null ? "—" : formatMetricValue(row.costPerConversion, "currency_per", currency);
  // CSV export reads tables; the cards are for people. This hidden table
  // carries the same figures one creative per column, so an export of Compare
  // is the comparison the screen shows.
  // The six figures on a card, in card order (two rows of three).
  const cardMetrics = (row: BreakdownRow): Array<{
    label: string;
    value: string;
  }> => [{
    label: "Spend",
    value: formatMetricValue(row.spend, "currency", currency)
  }, {
    label: "Impressions",
    value: formatMetricValue(row.impressions, "number", currency)
  }, {
    label: "Clicks",
    value: formatMetricValue(row.clicks, "number", currency)
  }, {
    label: "Conversions",
    value: formatMetricValue(row.conversions, "number", currency)
  }, {
    label: "Cost / conv.",
    value: costText(row)
  }, {
    label: "CTR",
    value: ctrText(row)
  }];
  const metricsByRow = new Map(rows.map(row => [row, cardMetrics(row)]));
  const exportRows: Array<{
    label: string;
    cells: Array<{
      text: string;
      title?: string;
    }>;
  }> = [{
    label: "Platform",
    cells: rows.map(row => ({
      text: platformLabel(row.platform)
    }))
  }, {
    label: "Format",
    cells: rows.map(row => ({
      text: row.format
    }))
  }, ...metricsByRow.get(rows[0]!)!.map(({
    label
  }, index) => ({
    label,
    cells: rows.map(row => ({
      text: metricsByRow.get(row)![index]!.value
    }))
  })), ...(showParts ? conversionParts.map(name => ({
    label: name,
    cells: rows.map(row => cellFor(row, name))
  })) : []), ...breakdowns.map(definition => ({
    label: definition.name,
    cells: rows.map(row => ({
      text: row.assignments?.filter(assignment => assignment.breakdownId === definition.id).map(assignment => assignment.name).join("; ") || "—"
    }))
  }))];
  return <>
      <table hidden aria-hidden="true">
        <thead>
          <tr>
            <th>Creative</th>
            {rows.map(row => <th key={`${row.platform}:${row.accountId}:${row.adId ?? row.name}`}>{row.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {exportRows.map(exportRow => <tr key={exportRow.label}>
              <th>{exportRow.label}</th>
              {exportRow.cells.map((cell, index) => <td key={rows[index]!.adId ?? rows[index]!.name} title={cell.title}>
                  {cell.text}
                </td>)}
            </tr>)}
        </tbody>
      </table>
      <div className="xrvj5dj xtfng45 x1l51sop xfisy6v" data-export-content style={columns === "auto" ? undefined : {
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
    }}>
        {rows.map(row => {
        const key = `${row.platform}:${row.accountId}:${row.adId ?? row.name}`;
        const poster = row.thumbnailUrl && !failedPreviews.has(row.thumbnailUrl) ? row.thumbnailUrl : null;
        const largePoster = isRenderablePreviewUrl(poster) ? poster : null;
        const open = onSelect ? () => onSelect(row) : undefined;
        const metrics = metricsByRow.get(row)!;
        const media = poster ? <img src={poster} alt="" loading="lazy" decoding="async" fetchPriority="low" onError={() => markFailed(row.thumbnailUrl)} referrerPolicy="no-referrer" className="xh8yej3 x5yr21d xl1xv1r" /> : <span className="x1hqrb6m x1s688f x1i6qc9y">{platformLabel(row.platform)}</span>;
        const hoverContent = row.previewMediaUrl || largePoster ? <CreativePeek videoUrl={row.previewMediaUrl ?? null} posterUrl={largePoster} fallback={platformLabel(row.platform)} /> : null;
        return <article key={key} className="x78zum5 xdt5ytf x5vihii xeuugli xw1tjjd xmkeg23 x1y0btm7 xcgbp8 xhrzdqn x1h1f4i9" aria-label={row.name}>
              {hoverContent ? <HoverCard content={hoverContent}>
                  <div className="xrvj5dj x1ku5rj1 xh8yej3 x1y5e3q9 xb3r6kr x16l1o21 xlcwlql xovanlf x1huxd7x" data-testid="compare-media">
                    {media}
                  </div>
                </HoverCard> : <div className="xrvj5dj x1ku5rj1 xh8yej3 x1y5e3q9 xb3r6kr x16l1o21 xlcwlql xovanlf x1huxd7x" data-testid="compare-media">
                  {media}
                </div>}
              <div className="x78zum5 xdt5ytf x166cprz xeuugli">
                {open ? <button type="button" {...stylex.props(recipes.bareButton, styles.compareName, recipes.truncate, styles.nameButton, recipes.focusRing)} title={`Open ${row.name}`} onClick={open}>
                    {row.name}
                  </button> : <strong {...stylex.props(styles.compareName, recipes.truncate)} title={row.name}>
                    {row.name}
                  </strong>}
                <div className="x1i6qc9y x1hqrb6m">
                  {platformLabel(row.platform)} · {row.format}
                  {row.entityType === "asset" ? ` · ${entityTypeLabel(row.entityType)}` : ""}
                </div>
                {breakdowns.map(definition => <AssignmentChips key={definition.id} row={row} breakdownId={definition.id} />)}
                <dl {...stylex.props(styles.compareMetrics, recipes.hairlineTopSoft)}>
                  {metrics.map(metric => <div key={metric.label} className="x78zum5 xdt5ytf x3d5knr xeuugli">
                      <dt {...stylex.props(styles.compareTerm, recipes.truncate)}>
                        {metric.label}
                      </dt>
                      <dd className="x1ghz6dp xgwkh9f xtu3avg xss6m8b">{metric.value}</dd>
                    </div>)}
                </dl>
                {showParts ? <dl {...stylex.props(styles.compareMetrics, styles.compareParts, recipes.hairlineTopSoft)}>
                    {conversionParts.map(name => {
                const cell = cellFor(row, name);
                return <div key={name} className="x78zum5 xdt5ytf x3d5knr xeuugli">
                          <dt {...stylex.props(styles.compareTerm, recipes.truncate)}>{name}</dt>
                          <dd title={cell.title} className="x1ghz6dp xgwkh9f xtu3avg xss6m8b">
                            {cell.text}
                          </dd>
                        </div>;
              })}
                  </dl> : null}
              </div>
            </article>;
      })}
      </div>
    </>;
}